import classNames from 'clsx';

interface YearlyAttendanceGridProps {
    data: Array<{
        month: string;
        month_num: number;
        percentage: number;
        present: number;
        absent: number;
        late: number;
        leave: number;
        missed?: number;
        expected_records: number;
        recorded_records?: number;
        working_days?: number;
        school_days?: number;
    }>;
    isLoading: boolean;
    onMonthClick?: (month: number) => void;
}

export default function YearlyAttendanceGrid({ data, isLoading, onMonthClick }: YearlyAttendanceGridProps) {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                    <div key={i} className="h-44 bg-slate-100 rounded-2xl animate-pulse" />
                ))}
            </div>
        );
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
        return <div className="text-center p-8 text-slate-500">No yearly data available.</div>;
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="yearly-attendance-grid">
            {data.map((item) => {
                const recorded = item.recorded_records ?? (item.present + item.absent + item.late + item.leave);
                const hasRecords = recorded > 0 || item.percentage > 0;
                const missed = item.missed ?? Math.max(0, item.expected_records - recorded);
                const denom = item.expected_records > 0 ? item.expected_records : (recorded + missed);
                const isOff = denom === 0 && !hasRecords;

                const pPct = denom > 0 ? ((item.present / denom) * 100).toFixed(1) : '0.0';
                const aPct = denom > 0 ? ((item.absent / denom) * 100).toFixed(1) : '0.0';
                const lPct = denom > 0 ? ((item.late / denom) * 100).toFixed(1) : '0.0';
                const lvPct = denom > 0 ? ((item.leave / denom) * 100).toFixed(1) : '0.0';
                const uPct = denom > 0 ? ((missed / denom) * 100).toFixed(1) : '0.0';

                let cardBg = 'bg-rose-600 border-rose-500 text-white shadow-sm';
                let textColor = 'text-white';
                let badgeStyle = 'bg-white/20 backdrop-blur-sm text-white';
                let barBg = 'bg-black/30';
                let borderSep = 'border-white/20';

                if (isOff) {
                    cardBg = 'bg-slate-200 border-2 border-slate-300';
                    textColor = 'text-slate-700';
                    badgeStyle = 'bg-slate-300 text-slate-800';
                    barBg = 'bg-slate-300';
                    borderSep = 'border-slate-300';
                } else if (!hasRecords) {
                    cardBg = 'bg-slate-100 border-2 border-dashed border-slate-300';
                    textColor = 'text-slate-600';
                    badgeStyle = 'bg-slate-200 text-slate-700';
                    barBg = 'bg-slate-200';
                    borderSep = 'border-slate-200';
                } else if (item.percentage >= 90) {
                    cardBg = 'bg-emerald-600 border-emerald-500 text-white shadow-sm';
                } else if (item.percentage >= 85) {
                    cardBg = 'bg-teal-600 border-teal-500 text-white shadow-sm';
                } else if (item.percentage >= 75) {
                    cardBg = 'bg-amber-600 border-amber-500 text-white shadow-sm';
                }

                const days = item.working_days ?? item.school_days;

                return (
                    <div
                        key={item.month_num}
                        onClick={() => onMonthClick?.(item.month_num)}
                        className={classNames(
                            "rounded-2xl p-4 flex flex-col justify-between h-[190px] relative overflow-hidden transition-all",
                            cardBg,
                            onMonthClick && "cursor-pointer hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                        )}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                                <div className={classNames("font-extrabold text-base leading-tight min-w-0 truncate", textColor)} title={item.month}>
                                    {item.month}
                                </div>
                                <div className={classNames("text-[10px] opacity-80 mt-0.5 truncate", textColor)}>
                                    {days !== undefined ? `${days} Days` : `Month ${item.month_num}`}
                                </div>
                            </div>
                            <span className={classNames("px-2.5 py-0.5 rounded-full text-xs font-black shrink-0", badgeStyle)}>
                                {hasRecords ? `${item.percentage}%` : isOff ? 'Off Month' : 'No Data'}
                            </span>
                        </div>

                        {/* V4 In-Tile Mini Multi-Segment Spark Bar (Thin 8px) */}
                        <div className="my-auto space-y-1.5">
                            <div className={classNames("w-full h-2 rounded-full overflow-hidden flex shadow-inner", barBg)}>
                                {item.present > 0 && (
                                    <div
                                        style={{ width: `${pPct}%` }}
                                        className="bg-emerald-400 h-full transition-all"
                                        title={`Present: ${item.present} (${pPct}%)`}
                                    />
                                )}
                                {item.absent > 0 && (
                                    <div
                                        style={{ width: `${aPct}%` }}
                                        className="bg-rose-400 h-full transition-all"
                                        title={`Absent: ${item.absent} (${aPct}%)`}
                                    />
                                )}
                                {item.late > 0 && (
                                    <div
                                        style={{ width: `${lPct}%` }}
                                        className="bg-amber-400 h-full transition-all"
                                        title={`Late: ${item.late} (${lPct}%)`}
                                    />
                                )}
                                {item.leave > 0 && (
                                    <div
                                        style={{ width: `${lvPct}%` }}
                                        className="bg-blue-400 h-full transition-all"
                                        title={`Leave: ${item.leave} (${lvPct}%)`}
                                    />
                                )}
                                {missed > 0 && (
                                    <div
                                        style={{ width: `${uPct}%` }}
                                        className="bg-slate-400 h-full transition-all"
                                        title={`Unmarked: ${missed} (${uPct}%)`}
                                    />
                                )}
                            </div>

                            {/* Metric Micro-Labels Under Spark Bar */}
                            <div className={classNames("flex justify-between text-[10px] font-semibold opacity-90", textColor)}>
                                <span>P: {pPct}%</span>
                                <span>A: {aPct}%</span>
                                <span>L: {lPct}%</span>
                                <span>Lv: {lvPct}%</span>
                                <span className={Number(uPct) > 0 ? (hasRecords ? "text-amber-200 font-extrabold" : "") : ""}>
                                    U: {uPct}%
                                </span>
                            </div>
                        </div>

                        {/* Footer Status */}
                        <div className={classNames("text-[10px] opacity-75 flex justify-between items-center border-t pt-1.5", borderSep, textColor)}>
                            <span>
                                {hasRecords ? `${item.present.toLocaleString()} Present` : 'No records'}
                            </span>
                            <span className="font-bold flex items-center gap-0.5">
                                Inspect ▾
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
